import React, { useState, useRef, useEffect } from 'react';
import { Plane, ChevronLeft, ChevronRight } from 'lucide-react';
import PlacementCard from '../../layouts/PlacementsCard';
import placements from '../../data/placementData';

const Placements = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 24;
  const topRef = useRef(null);
  const isFirstRender = useRef(true);

  const totalPages = Math.ceil(placements.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentPlacements = placements.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (topRef.current) {
      const yOffset = -100;
      const y = topRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen py-16 bg-gradient-to-b from-white to-red-50" ref={topRef}>
      <div className="mx-auto px-4">
        {/* Dynamic Header */}
        <div className="text-center mb-16 relative">
          <div className="inline-block relative">
            <h2 className="text-3xl sm:text-4xl font-medium">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-600 to-red-800">
                PROUD PLACEMENTS
              </span>
            </h2>
          </div>
          {/* Subtitle with plane icon */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <Plane className="text-red-600 rotate-45" size={20} />
            <p className="text-gray-600 sm:text-lg font-medium tracking-wide">
              WHERE CAREERS TAKE FLIGHT
            </p>
          </div>
        </div>

        {/* Decorative Background and Grid */}
        <div className="relative p-4 sm:p-8">
          {/* Decorative Background Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Corner Lines */}
            <div className="absolute top-0 left-0 w-16 h-16">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-transparent"></div>
              <div className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-red-600 to-transparent"></div>
            </div>
            <div className="absolute top-0 right-0 w-16 h-16">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-red-600 to-transparent"></div>
              <div className="absolute top-0 right-0 h-full w-1 bg-gradient-to-b from-red-600 to-transparent"></div>
            </div>
            <div className="absolute bottom-0 left-0 w-16 h-16">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-transparent"></div>
              <div className="absolute bottom-0 left-0 h-full w-1 bg-gradient-to-t from-red-600 to-transparent"></div>
            </div>
            <div className="absolute bottom-0 right-0 w-16 h-16">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-red-600 to-transparent"></div>
              <div className="absolute bottom-0 right-0 h-full w-1 bg-gradient-to-t from-red-600 to-transparent"></div>
            </div>

            {/* Subtle Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle,_transparent_20%,_#ef4444_20%,_#ef4444_25%,_transparent_25%,_transparent_35%,_#ef4444_35%,_#ef4444_40%,_transparent_40%)] bg-[length:24px_24px]"></div>
            </div>
          </div>

          {/* Grid Content */}
          <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-12">
            {currentPlacements.map((placement) => (
              <PlacementCard
                key={placement.id}
                company={placement.company}
                image={placement.image}
                copyImage={placement.copyImage}
              />
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="relative z-10 flex flex-col items-center gap-6 mt-16 pb-8">
              {/* Results Status */}
              <p className="text-sm text-gray-500 font-medium">
                Showing <span className="text-gray-900">{startIndex + 1}</span> to{' '}
                <span className="text-gray-900">
                  {Math.min(startIndex + ITEMS_PER_PAGE, placements.length)}
                </span>{' '}
                of <span className="text-gray-900">{placements.length}</span> placements
              </p>

              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 ${
                    currentPage === 1
                      ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                      : 'border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm'
                  }`}
                >
                  <ChevronLeft size={20} />
                </button>

                <div className="flex items-center gap-1 sm:gap-2">
                  {[...Array(totalPages)].map((_, i) => {
                    const pageNumber = i + 1;
                    // Show current page, first, last, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`w-10 h-10 rounded-full text-sm font-medium transition-all duration-300 ${
                            currentPage === pageNumber
                              ? 'bg-red-600 text-white shadow-md'
                              : 'text-gray-600 hover:bg-red-50 hover:text-red-600 border border-transparent hover:border-red-200'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber} className="text-gray-400 px-1">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-300 ${
                    currentPage === totalPages
                      ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                      : 'border-red-200 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 shadow-sm'
                  }`}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Placements;